import { HDate } from "@shared/src/datatypes/HDate";
import { HDateHour } from "@shared/src/datatypes/HDateHour";
import { DateRangeToken } from "@shared/src/enums/DateRangeToken";
import { SecurityController } from "@shared/src/controllers/security/security.controller";

export const DATERANGE_TOKEN_LABEL_KEYS: { [key: string]: string } = {
    [DateRangeToken.LAST1H]: "DATERANGE.LAST1H",
    [DateRangeToken.LAST6H]: "DATERANGE.LAST6H",
    [DateRangeToken.LAST12H]: "DATERANGE.LAST12H",
    [DateRangeToken.TODAY]: "DATERANGE.TODAY",
    [DateRangeToken.YESTERDAY]: "DATERANGE.YESTERDAY",
    [DateRangeToken.LAST2D]: "DATERANGE.LAST2D",
    [DateRangeToken.LAST3D]: "DATERANGE.LAST3D",
    [DateRangeToken.LAST7D]: "DATERANGE.LAST7D",
    [DateRangeToken.ALLTIME]: "DATERANGE.ALLTIME",
    [DateRangeToken.DAYMINUS5]: "DATERANGE.DAYMINUS5",
    [DateRangeToken.DAYMINUS4]: "DATERANGE.DAYMINUS4",
    [DateRangeToken.DAYMINUS3]: "DATERANGE.DAYMINUS3",
    [DateRangeToken.DAYMINUS2]: "DATERANGE.DAYMINUS2",
    [DateRangeToken.TOMORROW]: "DATERANGE.TOMORROW",
    [DateRangeToken.DAYPLUS2]: "DATERANGE.DAYPLUS2",
    [DateRangeToken.DAYPLUS3]: "DATERANGE.DAYPLUS3",
    [DateRangeToken.DAYPLUS4]: "DATERANGE.DAYPLUS4",
    [DateRangeToken.DAYPLUS5]: "DATERANGE.DAYPLUS5",
    [DateRangeToken.LASTMONTH]: "DATERANGE.LASTMONTH",
    [DateRangeToken.THISMONTH]: "DATERANGE.THISMONTH",
    [DateRangeToken.NEXTMONTH]: "DATERANGE.NEXTMONTH",
    [DateRangeToken.LASTYEAR]: "DATERANGE.LASTYEAR",
    [DateRangeToken.THISYEAR]: "DATERANGE.THISYEAR"
};

// Tokens de periode calendari (dia/mes/any relatius a avui) que el propi component
// resol immediatament a dateFrom/dateTo, a diferencia dels tokens tecnics (LAST1H...)
// que es deixen sense resoldre perque el backend els interpreti pel seu propi rellotge.
const CALENDAR_TOKEN_DAY_OFFSET: { [key: string]: number } = {
    [DateRangeToken.DAYMINUS5]: -5,
    [DateRangeToken.DAYMINUS4]: -4,
    [DateRangeToken.DAYMINUS3]: -3,
    [DateRangeToken.DAYMINUS2]: -2,
    [DateRangeToken.YESTERDAY]: -1,
    [DateRangeToken.TODAY]: 0,
    [DateRangeToken.TOMORROW]: 1,
    [DateRangeToken.DAYPLUS2]: 2,
    [DateRangeToken.DAYPLUS3]: 3,
    [DateRangeToken.DAYPLUS4]: 4,
    [DateRangeToken.DAYPLUS5]: 5
};

const CALENDAR_TOKEN_MONTH_OFFSET: { [key: string]: number } = {
    [DateRangeToken.LASTMONTH]: -1,
    [DateRangeToken.THISMONTH]: 0,
    [DateRangeToken.NEXTMONTH]: 1
};

const CALENDAR_TOKEN_YEAR_OFFSET: { [key: string]: number } = {
    [DateRangeToken.LASTYEAR]: -1,
    [DateRangeToken.THISYEAR]: 0
};

/***
 * Representa la selecció actual del date-range-picker: o bé un token relatiu
 * (Last 1 hour, Today...), que el backend resol amb el seu propi rellotge,
 * o bé un parell de dates absolutes (Start and end times / Around a time,
 * ja calculades pel front).
 */
export class DateRangeValueDto {

    public token: DateRangeToken = null;
    public dateFrom: HDateHour = null;
    public dateTo: HDateHour = null;

    public static relative(token: DateRangeToken): DateRangeValueDto {
        let result = new DateRangeValueDto();
        result.token = token;
        let calendarRange = DateRangeValueDto.resolveCalendarTokenRange(token);
        if (calendarRange) {
            result.dateFrom = calendarRange.dateFrom;
            result.dateTo = calendarRange.dateTo;
        }
        return result;
    }

    // Resol nomes els tokens de periode calendari (dia/mes/any); els tokens tecnics
    // (LAST1H, LAST6H...) queden sense resoldre pel backend, com fins ara.
    private static resolveCalendarTokenRange(token: DateRangeToken): { dateFrom: HDateHour, dateTo: HDateHour } {
        if (CALENDAR_TOKEN_DAY_OFFSET[token] !== undefined)
            return DateRangeValueDto.dayRange(CALENDAR_TOKEN_DAY_OFFSET[token]);
        if (CALENDAR_TOKEN_MONTH_OFFSET[token] !== undefined)
            return DateRangeValueDto.monthRange(CALENDAR_TOKEN_MONTH_OFFSET[token]);
        if (CALENDAR_TOKEN_YEAR_OFFSET[token] !== undefined)
            return DateRangeValueDto.yearRange(CALENDAR_TOKEN_YEAR_OFFSET[token]);
        return null;
    }

    // HDate.getMoment() retorna un moment en mode UTC pla, sense zona horaria assignada via
    // moment-timezone (.tz()), per això no podem fer servir HDateHour.buildFromMoment() aqui:
    // el seu getter value.tz() retornaria undefined i acabaria posant Timezone=undefined a l'HDateHour.
    private static dayRange(offsetDays: number): { dateFrom: HDateHour, dateTo: HDateHour } {
        let day = HDate.getMoment(HDate.today()).add(offsetDays, 'days');
        return {
            dateFrom: HDateHour.buildFromMomentByTZ(day.clone().startOf('day'), SecurityController.TIMEZONE, SecurityController.TIMEZONE),
            dateTo: HDateHour.buildFromMomentByTZ(day.clone().endOf('day'), SecurityController.TIMEZONE, SecurityController.TIMEZONE)
        };
    }

    private static monthRange(offsetMonths: number): { dateFrom: HDateHour, dateTo: HDateHour } {
        let month = HDate.getMoment(HDate.today()).add(offsetMonths, 'months');
        return {
            dateFrom: HDateHour.buildFromMomentByTZ(month.clone().startOf('month'), SecurityController.TIMEZONE, SecurityController.TIMEZONE),
            dateTo: HDateHour.buildFromMomentByTZ(month.clone().endOf('month'), SecurityController.TIMEZONE, SecurityController.TIMEZONE)
        };
    }

    private static yearRange(offsetYears: number): { dateFrom: HDateHour, dateTo: HDateHour } {
        let year = HDate.getMoment(HDate.today()).add(offsetYears, 'years');
        return {
            dateFrom: HDateHour.buildFromMomentByTZ(year.clone().startOf('year'), SecurityController.TIMEZONE, SecurityController.TIMEZONE),
            dateTo: HDateHour.buildFromMomentByTZ(year.clone().endOf('year'), SecurityController.TIMEZONE, SecurityController.TIMEZONE)
        };
    }

    public static absolute(dateFrom: HDateHour, dateTo: HDateHour): DateRangeValueDto {
        let result = new DateRangeValueDto();
        result.dateFrom = dateFrom;
        result.dateTo = dateTo;
        return result;
    }

    public static default(): DateRangeValueDto {
        return DateRangeValueDto.relative(DateRangeToken.LAST1H);
    }

    public isRelative(): boolean {
        return this.token != null;
    }

    public static isValid(value: DateRangeValueDto): boolean {
        if (value == null)
            return false;
        if (value.isRelative())
            return true;
        if (HDateHour.isNullOrNullValue(value.dateFrom) || HDateHour.isNullOrNullValue(value.dateTo))
            return false;
        return !HDateHour.isGreaterThan(value.dateFrom, value.dateTo);
    }

    public labelKey(): string {
        if (this.isRelative())
            return DATERANGE_TOKEN_LABEL_KEYS[this.token];
        return null;
    }

    public toHttpParams(): { [key: string]: string } {
        if (this.isRelative())
            return { range: this.token };

        return {
            dateFrom: HDateHour.getMoment(this.dateFrom).toISOString(),
            dateTo: HDateHour.getMoment(this.dateTo).toISOString()
        };
    }

    public static buildFromQueryParams(range: string, dateFrom: string, dateTo: string): DateRangeValueDto {
        if (range && Object.values(DateRangeToken).indexOf(<DateRangeToken>range) >= 0)
            return DateRangeValueDto.relative(<DateRangeToken>range);

        if (dateFrom && dateTo)
            return DateRangeValueDto.absolute(
                HDateHour.build(new Date(dateFrom), SecurityController.TIMEZONE, SecurityController.TIMEZONE),
                HDateHour.build(new Date(dateTo), SecurityController.TIMEZONE, SecurityController.TIMEZONE));

        return null;
    }
}
